import { apiClient, getAuthToken } from '../../../shared/api/client.js';
import type { Book } from '../../../shared/types/index.js';

export const booksApi = {
  async getBooks(params?: {
    status?: string;
    category?: string;
    search?: string;
    scope?: string;
  }): Promise<{ total: number; books: Book[]; roleScoping?: any }> {
    const searchParams = new URLSearchParams();
    if (params?.status) searchParams.append('status', params.status);
    if (params?.category) searchParams.append('category', params.category);
    if (params?.search) searchParams.append('search', params.search);
    if (params?.scope) searchParams.append('scope', params.scope);

    return apiClient(`/api/books?${searchParams.toString()}`);
  },

  async searchBooks(query: string): Promise<{
    query: string;
    count: number;
    isUibnFormat: boolean;
    books: Book[];
  }> {
    return apiClient(`/api/books/search/${encodeURIComponent(query)}`);
  },

  async getSearchSuggestions(query: string): Promise<{
    suggestions: Array<{
      id: string;
      uibn: string;
      title: string;
      authorName: string;
      publisherName: string;
      category: string;
      coverUrl?: string;
    }>;
  }> {
    return apiClient(`/api/books/suggestions?q=${encodeURIComponent(query)}`);
  },

  async getBookById(idOrUibn: string): Promise<{ book: Book }> {
    return apiClient(`/api/books/${encodeURIComponent(idOrUibn)}`);
  },

  async createBook(data: Partial<Book> & { publisherCode?: string }): Promise<{ message: string; book: Book }> {
    return apiClient('/api/books', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async bulkCreateBooks(booksList: any[]): Promise<{
    success: boolean;
    message: string;
    count: number;
    books: Book[];
  }> {
    return apiClient('/api/books/bulk', {
      method: 'POST',
      body: JSON.stringify({ books: booksList }),
    });
  },

  async updateBook(id: string, data: Partial<Book>): Promise<{ message: string; book: Book }> {
    return apiClient(`/api/books/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  async deleteBook(id: string): Promise<{ message: string }> {
    return apiClient(`/api/books/${id}`, {
      method: 'DELETE',
    });
  },

  async getShowcase(type: 'penulis' | 'penerbit' | 'organisasi', identifier: string): Promise<{
    type: 'penulis' | 'penerbit' | 'organisasi';
    identifier: string;
    profile: {
      id: string;
      name: string;
      role: string;
      organization?: string | null;
      publisherCode?: string | null;
      isPublisherVerified: boolean;
      publisherVerificationStatus?: string;
      publisherLegalName?: string | null;
      publisherWebsite?: string | null;
      publisherAddress?: string | null;
      publisherPhone?: string | null;
      profileImageUrl?: string | null;
      avatarUrl?: string | null;
      bio?: string | null;
      createdAt?: string | null;
    };
    stats: {
      totalBooks: number;
      totalEditions: number;
      pendingBooksCount?: number;
      categoryCount: number;
      yearRange: string;
    };
    categories: Array<{ category: string; count: number }>;
    books: Book[];
    pendingBooks?: Book[];
  }> {
    return apiClient(`/api/books/showcase/${type}/${encodeURIComponent(identifier)}`);
  },

  async getShowcaseDirectories(): Promise<{
    publishers: Array<{
      name: string;
      code?: string;
      verified: boolean;
      bookCount: number;
      sampleCover?: string;
      profileImageUrl?: string;
    }>;
    authors: Array<{
      name: string;
      verified: boolean;
      bookCount: number;
      sampleCover?: string;
      profileImageUrl?: string;
    }>;
    organizations?: Array<{
      name: string;
      code?: string;
      verified: boolean;
      bookCount: number;
      sampleCover?: string;
      profileImageUrl?: string;
    }>;
  }> {
    return apiClient('/api/books/showcase/directories');
  },

  async downloadCsvDirect(params?: {
    startDate?: string;
    endDate?: string;
    status?: string;
    category?: string;
  }): Promise<void> {
    const searchParams = new URLSearchParams();
    if (params?.startDate) searchParams.append('startDate', params.startDate);
    if (params?.endDate) searchParams.append('endDate', params.endDate);
    if (params?.status && params.status !== 'semua') searchParams.append('status', params.status);
    if (params?.category && params.category !== 'semua') searchParams.append('category', params.category);

    const token = getAuthToken();
    const res = await fetch(`/api/books/export/csv?${searchParams.toString()}`, {
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

    if (!res.ok) {
      if (res.status === 401 && token) {
        if (typeof window !== 'undefined') {
          window.dispatchEvent(
            new CustomEvent('uibn:session-expired', {
              detail: { message: 'Sesi Anda telah berakhir. Silakan masuk kembali.' },
            })
          );
        }
      }
      const err = await res.json().catch(() => ({ error: 'Gagal mengunduh CSV' }));
      throw new Error(err.error || 'Gagal mengunduh CSV');
    }

    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `katalog_uibn_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  },
};
